/**
 * RSA密码加密工具
 * 基于JSEncrypt库实现RSA公钥加密
 */

declare global {
  interface Window {
    JSEncrypt: any;
  }
}

class RSAPasswordEncryptor {
  private defaultPublicKey: string;
  private publicKey: string;

  constructor(customPublicKey: string | null = null) {
    // 默认RSA公钥（与原项目保持一致）
    this.defaultPublicKey = 'MIGfMA0GCSqGSIb3DQEBAQUAA4GNADCBiQKBgQCctQTweXAiaQ3ct5bhj6nyisOQiGmgC/hUdK+QO9I9DudcQSUMxIXvMtpiogB9RWkAUC4b86x7SiGD6aCp7PbTspd5fLf8F6LUIj/BtmktQq7JNsShjAWBxCkE49HIIvPvl9rt8lO7MkgS2vUT04tEYeu/62ltOc3BljJXoPC4pQIDAQAB';
    
    // 使用自定义公钥或默认公钥
    this.publicKey = customPublicKey || this.defaultPublicKey;
    
    // 检查JSEncrypt是否可用
    this.checkJSEncryptAvailability();
  }

  /**
   * 检查JSEncrypt库是否已加载
   */
  private checkJSEncryptAvailability(): void {
    if (typeof window === 'undefined' || typeof window.JSEncrypt === 'undefined') {
      throw new Error('JSEncrypt库未找到！请先引入JSEncrypt库');
    }
  }

  /**
   * 设置自定义RSA公钥
   */
  setPublicKey(publicKey: string): void {
    if (!publicKey || typeof publicKey !== 'string') {
      throw new Error('公钥必须是非空字符串');
    }
    this.publicKey = publicKey;
  }

  /**
   * 获取当前使用的公钥
   */
  getPublicKey(): string {
    return this.publicKey;
  }

  /**
   * 加密密码
   */
  encryptPassword(password: string): string | false {
    try {
      // 验证输入
      if (!password || typeof password !== 'string') {
        throw new Error('密码必须是非空字符串');
      }

      // 创建JSEncrypt实例
      const encryptor = new window.JSEncrypt();

      // 设置公钥
      encryptor.setPublicKey(this.publicKey);

      // 执行加密
      const encrypted = encryptor.encrypt(password);

      if (!encrypted) {
        throw new Error('加密失败，请检查公钥是否正确');
      }

      return encrypted;

    } catch (error) {
      console.error('RSA加密错误:', error);
      return false;
    }
  }

  /**
   * 验证公钥格式是否正确
   */
  validatePublicKey(publicKey: string): boolean {
    try {
      const encryptor = new window.JSEncrypt();
      encryptor.setPublicKey(publicKey);
      // 尝试加密一个测试字符串
      const testResult = encryptor.encrypt('test');
      return !!testResult;
    } catch (error) {
      return false;
    }
  }
}

// 创建全局实例（使用默认公钥）
let rsaEncryptor: RSAPasswordEncryptor | null = null;

/**
 * 获取RSA加密器实例
 */
export const getRSAEncryptor = (): RSAPasswordEncryptor => {
  if (!rsaEncryptor) {
    rsaEncryptor = new RSAPasswordEncryptor();
  }
  return rsaEncryptor;
};

/**
 * 便捷函数：加密密码
 */
export const encryptPassword = (password: string, customPublicKey?: string): string | false => {
  if (customPublicKey) {
    const customEncryptor = new RSAPasswordEncryptor(customPublicKey);
    return customEncryptor.encryptPassword(password);
  }
  return getRSAEncryptor().encryptPassword(password);
};

/**
 * 便捷函数：设置全局公钥
 */
export const setGlobalPublicKey = (publicKey: string): void => {
  getRSAEncryptor().setPublicKey(publicKey);
};

export { RSAPasswordEncryptor };
